import { CustomerKYCData } from "@/core/customers/entities/CustomerKYC";
import { CustomerKYCUpdateDTO } from "@/core/customers/entities/CustomerKYCUpdate";
import GetCustomerKYCUseCase from "@/core/customers/useCases/GetCustomerKYCUseCase/GetCustomerKYCUseCase";
import UpdateCustomerKYCUseCase from "@/core/customers/useCases/UpdateCustomerKYCUseCase/UpdateCustomerKYCUseCase";
import UpdateAdditionalInformationUseCase from "@/core/customers/useCases/UpdateAdditionalInformationUseCase/UpdateAdditionalInformationUseCase";
import type {
  AdditionalInformationUpdates,
  FicheClient360State,
  FicheClient360StoreDeps,
  KYCUpdateResult,
} from "../types";
import {
  createFetchAction,
  isException,
  mergeKycDataWithUpdates,
} from "../helpers";

export function createKycSlice(
  deps: FicheClient360StoreDeps,
  set: (
    partial:
      | Partial<FicheClient360State>
      | ((state: FicheClient360State) => Partial<FicheClient360State>),
  ) => void,
  get: () => FicheClient360State,
): Partial<FicheClient360State> {
  // Keep get in the signature for future extensibility while avoiding noUnusedLocals errors
  void get;

  const getCustomerKYCUseCase = new GetCustomerKYCUseCase(
    deps.customerKYCRepository,
  );
  const updateCustomerKYCUseCase = new UpdateCustomerKYCUseCase(
    deps.customerKYCRepository,
  );
  const updateAdditionalInformationUseCase =
    new UpdateAdditionalInformationUseCase(deps.customerKYCRepository);

  return {
    fetchKYC: createFetchAction<CustomerKYCData | null>(
      set,
      "kycLoading",
      "kycError",
      "kycData",
      (businessKey) => getCustomerKYCUseCase.execute(businessKey),
      "Erreur lors du chargement des données KYC :",
      () => null,
    ),

    updateCustomerKYC: async (
      businessKey: string,
      updates: Partial<CustomerKYCUpdateDTO>,
    ): Promise<KYCUpdateResult> => {
      set({ updateLoading: true, updateError: null });
      try {
        const success = await updateCustomerKYCUseCase.execute({
          businessKey,
          updates,
        });
        if (success) {
          set((state: FicheClient360State) => {
            if (!state.kycData || state.kycData.businessKey !== businessKey) {
              return {};
            }

            return {
              kycData: mergeKycDataWithUpdates(state.kycData, updates),
            };
          });
        }
        return { success };
      } catch (error) {
        const exception = isException(error) ? error : null;
        set({ updateError: exception });
        return {
          success: false,
          errorMessage: (exception?.message as string) ?? undefined,
        };
      } finally {
        set({ updateLoading: false });
      }
    },

    updateAdditionalInformation: async (
      businessKey: string,
      updates: AdditionalInformationUpdates,
    ): Promise<KYCUpdateResult> => {
      set({ updateLoading: true, updateError: null });
      try {
        const success = await updateAdditionalInformationUseCase.execute({
          businessKey,
          updates,
        });
        if (success) {
          set((state: FicheClient360State) => {
            if (!state.kycData || state.kycData.businessKey !== businessKey) {
              return {};
            }

            return {
              kycData: mergeKycDataWithUpdates(state.kycData, updates),
            };
          });
        }
        return { success };
      } catch (error) {
        const exception = isException(error) ? error : null;
        set({ updateError: exception });
        return {
          success: false,
          errorMessage: (exception?.message as string) ?? undefined,
        };
      } finally {
        set({ updateLoading: false });
      }
    },
  };
}
